import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import './PrecedentViewer.css';

const PrecedentViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const precedentDataRef = useRef([]);
    const isMounted = useRef(false);
    const [precedentNumberFilter, setPrecedentNumberFilter] = useState('');
    const [headingFilter, setHeadingFilter] = useState('');
    const [textFilter, setTextFilter] = useState('');
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => [
        'Precedent Number',
        'Heading',
        'Description',
        'Reason'
    ], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            background: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#fff' : '#495057',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#ccc' : '#495057',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#precedentTable').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadPrecedentData = async () => {
            if (precedentDataRef.current.length > 0) return;

            try {
                const response = await fetch('/Precedents.json');
                if (!response.ok) throw new Error('Failed to fetch Precedents data');
                const jsonData = await response.json();

                // Flatten the data structure
                if (!Array.isArray(jsonData)) {
                    throw new Error('Precedents.json is not a valid array');
                }

                const flattenedData = jsonData.reduce((acc, section) => {
                    const records = section?.Records || [];
                    records.forEach((record) => {
                        acc.push({
                            'Precedent Number': record.Precedent_Number || '',
                            'Heading': record.Heading || '',
                            'Description': record.Description || '',
                            'Reason': record.Reason || '',
                        });
                    });
                    return acc;
                }, []);

                if (!flattenedData.length) {
                    throw new Error('No precedent records found in Precedents.json');
                }

                precedentDataRef.current = flattenedData;
            } catch (error) {
                console.error('Precedent load error:', error);
                alert(`Failed to load Precedents data: ${error.message || 'Please try again later.'}`);
                precedentDataRef.current = [];
            }

            if (!tableRef.current) return;

            if (dataTableRef.current) {
                dataTableRef.current.destroy(false);
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data) => data || ''
                        })),
                        columnDefs: [
                            { width: '120px', targets: 0 },
                            { width: '100px', targets: 1 },
                            { width: 'auto', targets: [2, 3] }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [[0, 'asc']],
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>'
                    });
                    dataTableRef.current.clear().rows.add(precedentDataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    alert('Failed to initialize table.');
                }
            }
        };

        loadPrecedentData();

        return () => {
            if (dataTableRef.current && !document.getElementById('precedentTable')) {
                dataTableRef.current.destroy(false);
            }
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) return;
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const precedentNumberFilterFunction = (settings, data, dataIndex) => {
                if (settings.nTable.id !== 'precedentTable') return true;
                const precedentNumber = String(data[0] || '');
                return precedentNumberFilter ? precedentNumber.includes(precedentNumberFilter) : true;
            };

            const textFilterFunction = (settings, data, dataIndex) => {
                if (settings.nTable.id !== 'precedentTable') return true;
                if (!textFilter) return true;
                const term = textFilter.toLowerCase();
                const description = String(data[2] || '').toLowerCase();
                const reason = String(data[3] || '').toLowerCase();
                return description.includes(term) || reason.includes(term);
            };

            if (precedentNumberFilter) {
                $.fn.dataTable.ext.search.push(precedentNumberFilterFunction);
            }
            if (textFilter) {
                $.fn.dataTable.ext.search.push(textFilterFunction);
            }

            try {
                dataTableRef.current
                    .column(0)
                    .search('', false, false)
                    .column(1)
                    .search(headingFilter, false, true)
                    .column(2)
                    .search('', false, true)
                    .column(3)
                    .search('', false, true)
                    .draw();
            } catch (error) {
                isDrawing.current = false;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [precedentNumberFilter, headingFilter, textFilter, isTableInitialized]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    const handlePrecedentNumberFilterChange = useCallback((e) => {
        setPrecedentNumberFilter(e.target.value);
    }, []);

    const handleHeadingFilterChange = useCallback((e) => {
        setHeadingFilter(e.target.value);
    }, []);

    const handleTextFilterChange = useCallback((e) => {
        setTextFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setPrecedentNumberFilter('');
            setHeadingFilter('');
            setTextFilter('');
            dataTableRef.current
                .column(0)
                .search('')
                .column(1)
                .search('')
                .column(2)
                .search('')
                .column(3)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="precedent-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Customs Tariff Precedents</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>    
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <input
                                type="text"
                                placeholder="Filter Precedent Number..."
                                value={precedentNumberFilter}
                                onChange={handlePrecedentNumberFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Heading..."
                                value={headingFilter}
                                onChange={handleHeadingFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Description or Reason..."
                                value={textFilter}
                                onChange={handleTextFilterChange}
                                className="custom-filter-input custom-filter-input-wide"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="precedentTable" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default PrecedentViewer;